import type { Widget, WidgetType } from '@/types';

const PAGEVIEW = 'Pageview';

// Built-in widgets that are always rendered on the dashboard when the
// project has Pageview events. Pulled out of widget-grid.tsx so the route
// loader can also see them and warm their aggregation queries up front.
// A precondition a surface needs before it is worth showing at all. Distinct
// from "empty this range": an unmet requirement can never produce data.
export type WidgetRequirement =
  | { kind: 'event'; name: string }
  | { kind: 'connection'; integrationId: string };

export type WidgetVisibilityContext = {
  eventNames: string[];
  connectedIntegrationIds: string[];
};

export type PageviewWidget = {
  internal: boolean;
  type: WidgetType;
  requires?: WidgetRequirement;
  params: {
    title: string;
    eventName?: string;
    property?: string;
    sumProperty?: string;
  };
};

export function meetsRequirement(
  requirement: WidgetRequirement | undefined,
  context: WidgetVisibilityContext,
): boolean {
  if (!requirement) return true;
  return requirement.kind === 'event'
    ? context.eventNames.includes(requirement.name)
    : context.connectedIntegrationIds.includes(requirement.integrationId);
}

export const PAGEVIEW_WIDGETS: PageviewWidget[] = [
  {
    internal: true,
    type: 'AGGREGATION_BY_PROPERTY',
    params: { title: 'Top Pages', eventName: PAGEVIEW, property: '_url' },
  },
  {
    internal: true,
    type: 'AGGREGATION_BY_PROPERTY',
    params: {
      title: 'Landing Pages',
      property: '_entry_url',
      eventName: PAGEVIEW,
    },
  },
  {
    internal: true,
    type: 'REFERRER',
    params: { title: 'Top Sources' },
  },
  {
    internal: true,
    type: 'AGGREGATION_BY_PROPERTY',
    params: {
      title: 'Channels',
      property: '_acquisition_channel',
      eventName: PAGEVIEW,
    },
  },
  {
    internal: true,
    type: 'COUNTRY',
    params: { title: 'Countries' },
  },
  {
    internal: true,
    type: 'AGGREGATION_BY_PROPERTY',
    params: {
      title: 'Cities',
      property: '_location_city',
      eventName: PAGEVIEW,
    },
  },
  {
    internal: true,
    type: 'AGGREGATION_BY_PROPERTY',
    params: { title: 'Screens', property: '_screen', eventName: PAGEVIEW },
  },
  {
    internal: true,
    type: 'AGGREGATION_BY_PROPERTY',
    params: { title: 'Devices', property: '_device', eventName: PAGEVIEW },
  },
  {
    internal: true,
    type: 'AGGREGATION_BY_PROPERTY',
    params: { title: 'Browsers', property: '_browser', eventName: PAGEVIEW },
  },
  {
    internal: true,
    type: 'AGGREGATION_BY_PROPERTY',
    params: {
      title: 'Operating Systems',
      property: '_os',
      eventName: PAGEVIEW,
    },
  },
  {
    internal: true,
    type: 'CAMPAIGNS',
    params: { title: 'Campaigns' },
  },
];

// For each widget type that ultimately renders an AggregationWidget under
// the hood, return the eventName + property pair so the route loader can
// prefetch aggregationByPropertyOptions(...) before the widget mounts. The
// mapping mirrors the per-widget defaults set in
// referrer-widget / country-widget / campaigns-widget.
export function widgetToAggregationParams(
  widget: Widget | PageviewWidget,
): { eventName: string; property: string; sumProperty?: string } | null {
  switch (widget.type) {
    case 'AGGREGATION_BY_PROPERTY': {
      const eventName = widget.params?.eventName;
      const property = widget.params?.property;
      if (!eventName || !property) return null;
      return { eventName, property, sumProperty: widget.params?.sumProperty };
    }
    case 'REFERRER':
      return { eventName: PAGEVIEW, property: '_referrer' };
    case 'COUNTRY':
      return { eventName: PAGEVIEW, property: '_location_country' };
    case 'CAMPAIGNS':
      // CampaignsWidget initial render uses utm_source; subsequent options
      // (utm_medium, utm_campaign, utm_content, utm_term) lazy-load on
      // click — fine to leave those cold.
      return { eventName: PAGEVIEW, property: 'utm_source' };
    default:
      return null;
  }
}
